import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const arrowLeft = "/images/leftArrow.png";
const arrowRight = "/images/rightArrow.png";

const Carousel = ({
  items,
  desktopRows = 1,
  desktopColumns = 3,
  mobileRows = 1,
  mobileColumns = 1,
  dotsColor = "#000",
  labelBgColor = "green",
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesPerPage, setSlidesPerPage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Resize logic
  useEffect(() => {
    const handleResize = () => {
      const mobileView = window.innerWidth <= 768;
      setIsMobile(mobileView);

      const calculatedSlides = mobileView
        ? mobileRows * mobileColumns
        : desktopRows * desktopColumns;

      setSlidesPerPage(calculatedSlides);
      setIsReady(true);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [desktopRows, desktopColumns, mobileRows, mobileColumns]);

  if (!isReady || slidesPerPage === 0) {
    return (
      <div className="flex justify-center items-center min-h-[300px] text-lg text-gray-500">
        Cargando...
      </div>
    );
  }

  const totalPages = Math.ceil(items.length / slidesPerPage);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex >= items.length - slidesPerPage) return 0;
      const nextIndex = prevIndex + slidesPerPage;
      return nextIndex >= items.length ? 0 : nextIndex;
    });
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex === 0) {
        const substraction =
          items.length % slidesPerPage
            ? items.length % slidesPerPage
            : slidesPerPage;
        return Math.max(0, items.length - substraction);
      }
      return Math.max(0, prevIndex - slidesPerPage);
    });
  };

  const goToSlide = (pageIndex) => {
    const newIndex = pageIndex * slidesPerPage;
    setCurrentIndex(
      newIndex < items.length
        ? newIndex
        : Math.max(0, items.length - slidesPerPage)
    );
  };

  // Swipe handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipe();
  };

  const handleSwipe = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }
  };

  // Grid styles
  const gridStyle = {
    gridTemplateRows: `repeat(${isMobile ? mobileRows : desktopRows}, 1fr)`,
    gridTemplateColumns: `repeat(${
      isMobile ? mobileColumns : desktopColumns
    }, 1fr)`,
  };

  return (
    <div
      className="w-full px-3 sm:px-0 relative"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="flex items-center justify-between gap-3 relative">
        {/* Hide arrows on mobile */}
        {!isMobile && (
          <button
            className="flex items-center justify-center z-10 relative flex-shrink-0 hover:scale-110 transition-transform"
            onClick={prevSlide}
          >
            <img
              src={arrowLeft}
              alt="Anterior"
              className="w-20 h-20 lg:w-24 lg:h-24 object-contain"
            />
          </button>
        )}

        <div
          className="grid gap-2 sm:gap-4 w-full flex-grow"
          style={gridStyle}
        >
          {items
            .slice(currentIndex, currentIndex + slidesPerPage)
            .map((item, index) => (
              <div
                key={`${currentIndex}-${index}`}
                className="relative overflow-hidden rounded-xl shadow-md hover:-translate-y-1 hover:shadow-xl transition-transform aspect-square"
              >
                <Link to={item.link} className="relative w-full h-full flex flex-col">
                  <img
                    src={item.image}
                    alt={`Slide ${index}`}
                    className={`w-full h-full ${
                      item.imageFit === "contain"
                        ? "object-contain object-center p-2"
                        : "object-cover object-center"
                    }`}
                    style={{ backgroundColor: item.imageBg || "transparent" }}
                  />
                  <div
                    className="absolute bottom-0 left-0 w-full py-1.5 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm text-white text-center font-bold"
                    style={{ backgroundColor: labelBgColor }}
                  >
                    {item.label}
                  </div>
                </Link>
              </div>
            ))}
        </div>

        {!isMobile && (
          <button
            className="flex items-center justify-center z-10 relative flex-shrink-0 hover:scale-110 transition-transform"
            onClick={nextSlide}
          >
            <img
              src={arrowRight}
              alt="Siguiente"
              className="w-20 h-20 lg:w-24 lg:h-24 object-contain"
            />
          </button>
        )}
      </div>

      {/* More bottom margin for dots */}
      <div className="flex justify-center mt-6 mb-10 sm:mb-6 gap-3 sm:gap-4">
        {Array.from({ length: totalPages }).map((_, index) => (
          <button
            key={index}
            className={`rounded-full cursor-pointer transition-transform w-3 h-3 sm:w-3.5 sm:h-3.5 ${
              currentIndex === index * slidesPerPage
                ? "opacity-100 scale-110"
                : "opacity-50 hover:scale-110"
            }`}
            style={{ backgroundColor: dotsColor }}
            onClick={() => goToSlide(index)}
            aria-label={`Ir a slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
